/* oxlint-disable react-hooks-js/immutability */
/* eslint-disable react-hooks/immutability */

import { SubmitEvent, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Close, Information } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  Divider,
  DrawerDescription,
  DrawerTitle,
  typographyVariants,
} from '@mint-lib/tw-components';
import { proxy } from 'valtio';

import type {
  GranularityLevel,
  TemplateListItem,
  TemplateListItemV1,
  TemplateListItemV2,
  TemplatePreview,
} from '../../../api/types.js';
import { useLocalizedGranularityLevelLabels } from '../../../constants/i18n/granularityLevel.js';
import { useImportHubV2 } from '../../../hooks/ff/index.js';
import {
  useCreateTemplate,
  useDataFileFormatsOptions,
} from '../../../queries/index.js';
import {
  getDateFormatLabel,
  isCanonicalAPIError,
  parseApiError,
} from '../../../utils/index.js';
import { Input } from '../../atoms/Input.jsx';
import { Select } from '../../atoms/Select.jsx';
import { TemplateDrawer } from '../../atoms/TemplateDrawer.jsx';
import { Tooltip } from '../../atoms/Tooltip.jsx';
import { FooterRowsControl } from './FooterRowsControl.jsx';
import { validateForm } from './formValidator.js';
import { TemplateContextFields } from './TemplateContextFields.jsx';
import { TemplateDuplicateSubmitButton } from './TemplateDuplicateSubmitButton.jsx';
import { TemplateHeadersMappingTable } from './TemplateHeadersMappingTable.jsx';
import { TemplateNameInput } from './TemplateNameInput.jsx';
import type { TemplateDuplicationFormState } from './types.js';

type TemplateDuplicateDrawerProps = Readonly<{
  template: TemplateListItem | null;
  onSuccess: () => void;
  onDrawerClose: () => void;
}>;

export const TemplateDuplicateDrawer = ({
  template,
  onSuccess,
  onDrawerClose,
}: TemplateDuplicateDrawerProps) => {
  const isOpen = !!template;

  return (
    <TemplateDrawer open={isOpen} onClose={onDrawerClose}>
      {isOpen && (
        <TemplateDuplicateDrawerContent
          template={template}
          onSuccess={onSuccess}
          onDrawerClose={onDrawerClose}
        />
      )}
    </TemplateDrawer>
  );
};

type TemplateDuplicateDrawerContentProps = Readonly<{
  template: TemplateListItem;
  onSuccess: () => void;
  onDrawerClose: () => void;
}>;

const TemplateDuplicateDrawerContent = ({
  template,
  onSuccess,
  onDrawerClose,
}: TemplateDuplicateDrawerContentProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const isImportHubV2 = useImportHubV2();

  const { data: tableHeadersFormatsMeta, isLoading: isFormatsLoading } =
    useDataFileFormatsOptions({ throwOnError: true });

  const granularityLevelLabelByType = useLocalizedGranularityLevelLabels();

  const getGranularityLevelLabel = (level: GranularityLevel) =>
    granularityLevelLabelByType[level];

  const { mutateAsync: createTemplate, isPending: isSubmitting } =
    useCreateTemplate();

  const [state] = useState(
    () =>
      proxy({
        form: {
          name: template.name,
          sheetName: template.sheetName,
          journeyId: (template as TemplateListItemV1).campaignId,
          journeyName: (template as TemplateListItemV1).campaign,
          mediaPlanId: (template as TemplateListItemV2).mediaPlanId,
          mediaPlanName: (template as TemplateListItemV2).mediaPlanName,
          activityId: (template as TemplateListItemV2).activityId,
          activityName: (template as TemplateListItemV2).activityName,
          platformName: template.platform,
          granularityLevel: template.granularityLevel,
          dateOrder: template.dateOrder,
          numberFormat: template.numberFormat,
          footerRowsCount: template.footerRowsCount || 0,
          headerRow: [],
          headers: [],
          headerOptions: [],
        },
        get validationError() {
          return validateForm({
            t,
            formValues: state.form,
            isImportHubV2,
          });
        },
        ui: {
          isFooterRowsEnabled: Boolean(template.footerRowsCount),
          submissionError: null as string | null,
        },
      }) as TemplateDuplicationFormState,
  );

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      const ingestionContext = Object.fromEntries(
        Object.entries({
          journeyId: state.form.journeyId,
          journeyName: state.form.journeyName,
          mediaPlanId: state.form.mediaPlanId,
          mediaPlanName: state.form.mediaPlanName,
          activityId: state.form.activityId,
          activityName: state.form.activityName,
          platformName: state.form.platformName,
        }).filter(([_, value]) => Boolean(value)),
      ) as TemplatePreview['ingestionContext'];

      const headerRow = state.form.headerRow.map((headerRowCell, index) => {
        const matchingHeader = state.form.headers.find(
          (header) => header.columnPosition === index + 1,
        );
        if (matchingHeader) {
          return matchingHeader.rawHeader;
        }
        return headerRowCell;
      });

      await createTemplate({
        data: {
          name: state.form.name,
          sheetName: state.form.sheetName,
          granularityLevel: state.form.granularityLevel,
          ingestionContext,
          formats: {
            numberFormat: state.form.numberFormat,
            dateOrder: state.form.dateOrder,
          },
          headerRow,
          headers: state.form.headers.filter(
            (header) => header.mappedHeaderName,
          ),
          templateHeaders: state.form.headers,
          footerRowsCount: state.form.footerRowsCount,
        },
      });
      onSuccess();
      onDrawerClose();
    } catch (error) {
      const parsedError = parseApiError(error);
      if (isCanonicalAPIError(parsedError)) {
        if (parsedError.type === 'DuplicatedTemplateNameError') {
          state.ui.submissionError = parsedError.type;
          return;
        }

        state.ui.submissionError = parsedError.type;
        return;
      }

      enqueueSnackbar({
        variant: 'error',
        title: t('Server error'),
        subTitle: t('Failed to create a template'),
        removable: true,
      });
    }
  };

  const dateFormatsOptions = tableHeadersFormatsMeta.dateOrders.map(
    (value) => ({
      value,
      label: getDateFormatLabel(t, value),
    }),
  );

  return (
    <form onSubmit={handleSubmit} data-testid="template-duplicate-drawer">
      <div className="flex flex-1 flex-col gap-4 p-2">
        <div className="mb-1 flex items-center">
          <Button
            type="button"
            aria-label={t('Close')}
            data-dd-action-name="Close"
            onClick={onDrawerClose}
            icon
            variant="text"
            size="small"
            color="secondary"
            className="mr-2"
          >
            <Close />
          </Button>
          <DrawerTitle>{t('Duplicate template')}</DrawerTitle>
          <DrawerDescription className="sr-only">
            {t('Create a new template based on this one.')}
          </DrawerDescription>
          <TemplateDuplicateSubmitButton
            state={state}
            isSubmitting={isSubmitting}
          />
        </div>
        <TemplateNameInput state={state} />
        <Input
          label={t('Sheet name')}
          infoIconWithTooltip={t('Optional. Used only for Excel files.')}
          defaultValue={state.form.sheetName ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            state.form.sheetName = e.target.value.trim();
          }}
          size="medium"
          autoComplete="off"
        />
        <Divider />
        <div className="flex items-center gap-1">
          <h4 className={typographyVariants({ variant: 'h4' })}>
            {t('Ingestion context')}
          </h4>
          <Tooltip description={t('Select the required ingestion fields.')}>
            <div className="flex items-center">
              <Information className="fill-icon-secondary" />
            </div>
          </Tooltip>
        </div>
        <TemplateContextFields state={state} />
        <Input
          label={t('Data attribution level*')}
          defaultValue={getGranularityLevelLabel(state.form.granularityLevel)}
          size="medium"
          readOnly
          autoComplete="off"
        />
        <Divider />
        <h4 className={typographyVariants({ variant: 'h4' })}>
          {t('File format')}
        </h4>
        <div className="flex w-full justify-stretch gap-4">
          <Select
            label={t('Date format*')}
            infoIconWithTooltip={t('Pick the format used in your file.')}
            placeholder={t('Select')}
            defaultInputValue={getDateFormatLabel(t, template.dateOrder)}
            options={dateFormatsOptions}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            onChange={(selectedOption) => {
              state.form.dateOrder = selectedOption?.value || '';
            }}
            isLoading={isFormatsLoading}
            className="w-[186px]"
          />
          <Select
            label={t('Number format')}
            infoIconWithTooltip={t(
              'Specify the number format to interpret decimals.',
            )}
            placeholder={t('Select')}
            defaultInputValue={state.form.numberFormat}
            options={tableHeadersFormatsMeta.numberFormats}
            onChange={(selectedOption) => {
              state.form.numberFormat = selectedOption;
            }}
            isLoading={isFormatsLoading}
            className="w-[126px]"
          />
          <FooterRowsControl state={state} />
        </div>
        <Divider />
        <TemplateHeadersMappingTable template={template} state={state} />
      </div>
    </form>
  );
};
